import { CommonModule } from '@angular/common'
import { Component, DestroyRef, EventEmitter, inject, Input, OnInit, Output, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { Alert } from '@app/v2/shared/enums/alert.enum'
import { AlertService } from '@app/v2/shared/services/alert.service'
import { AuthService } from '@auth/services/auth.service'
import { environment } from '@environments/environment'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { loadStripe } from '@stripe/stripe-js/pure'
import { PlanPrice, Price } from '@workspaces/models/plan-price.interface'

/**
 * Holds information about available workspace plans
 */
@Component({
  selector: 'ngx-workspaces-invoice-plans',
  templateUrl: './invoice-plans.component.html',
  imports: [CommonModule, TranslateModule],
  standalone: true
})
export class InvoicePlansComponent implements OnInit {
  private auth = inject(AuthService)
  private translate = inject(TranslateService)
  private alertService = inject(AlertService)
  /** Was it fired because the user tried to create another workspace */
  @Input() createWorkspace = false

  /** Event emitted when the dialog should be closed */
  @Output() closeDialog = new EventEmitter<void>()

  // Signals
  private _ready = signal(false)
  private _processing = signal(false)
  private _proPlan = signal<PlanPrice | null>(null)
  private _basicPlan = signal<PlanPrice | null>(null)
  private _selectedPrice = signal<Price | null>(null)

  // Computed signals
  ready = this._ready.asReadonly()
  processing = this._processing.asReadonly()
  proPlan = this._proPlan.asReadonly()
  basicPlan = this._basicPlan.asReadonly()
  selectedPrice = this._selectedPrice.asReadonly()

  private readonly _destroyRef = inject(DestroyRef)

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.auth
      .getPlansWithPrices()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe((result) => {
        const basicPlan = result.find((o) => o.id === 1)
        const proPlan = result.find((o) => o.id === 2)

        this._basicPlan.set(basicPlan)
        this._proPlan.set(proPlan)
        this._ready.set(true)

        // Reverse the product price to get the last item
        proPlan?.productPrice.reverse()
        if (proPlan?.productPrice?.length) {
          this._selectedPrice.set(proPlan.productPrice[2])
        }
      })
  }

  /**
   * Upgrades from Basic to Pro plan
   */
  initiateUpgrade() {
    const currentPrice = this._selectedPrice()
    if (!currentPrice) return

    this._processing.set(true)

    this.auth
      .canUpgradeOrganization()
      .pipe(takeUntilDestroyed(this._destroyRef))
      .subscribe({
        next: (isEligible: boolean) => {
          if (!isEligible) {
            this._processing.set(false)
            this.alertService.alert(Alert.WARNING, this.translate.instant('v2.invoicePlans.emailSentToAdmin'))
          } else {
            this.auth
              .createSubscription(currentPrice.id)
              .pipe(takeUntilDestroyed(this._destroyRef))
              .subscribe({
                next: (result: any) => {
                  switch (result.object) {
                    case 'checkout.session':
                      this.redirectToCheckout(result.id)
                      break
                    case 'billing_portal.session':
                      window.open(result.url, '_blank')
                      this._processing.set(false)
                      break
                  }
                },
                error: () => this._processing.set(false)
              })
          }
        },
        error: () => {
          this._processing.set(false)
          this.alertService.alert(Alert.ERROR, this.translate.instant('v2.invoicePlans.eligibilityCheckError'))
        }
      })
  }

  /**
   * Async redirect to checkout
   * @param checkoutId string
   */
  async redirectToCheckout(checkoutId: string) {
    const stripe = await loadStripe(environment.stripeKey)
    try {
      await stripe.redirectToCheckout({
        sessionId: checkoutId
      })
    } catch (error) {
      console.error(error)
    }
    this._processing.set(false)
  }

  /**
   * Closes the dialog
   */
  close() {
    this.closeDialog.emit()
  }

  /**
   * Updates the selected price
   */
  updateSelectedPrice(price: Price) {
    this._selectedPrice.set(price)
  }

  /**
   * Gets the interval text for display
   */
  getIntervalText(price: Price): string {
    const { interval, interval_count } = price.recurring

    if (this.translate.currentLang === 'ar') {
      if (interval === 'year') {
        return interval_count === 1 ? 'سنة واحدة' : `${interval_count} سنة`
      }

      if (interval === 'month') {
        return interval_count === 1 ? 'شهر واحد' : `${interval_count} شهور`
      }
    }

    // Default translation for other languages
    const translatedInterval = this.translate.instant(`v2.invoicePlans.intervals.${interval}`)
    return `${interval_count} ${translatedInterval}`
  }
}
